import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    X,
    Search,
    Users,
    CheckCircle2,
    LogIn,
    LogOut,
    Camera,
    Phone,
    Building2,
    Car,
    Bike,
    Sparkles,
    Plus,
    RefreshCw,
    Clock,
    User,
    Baby,
    Coffee,
    Newspaper,
    Utensils,
    Shirt,
    Wrench,
    Check,
    Mic,
    MicOff,
    QrCode
} from "lucide-react";
import ShareWhatsApp from "@/components/ShareWhatsApp";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import { formatSpokenName, formatSpokenPhone } from "@/pages/Guard";

const SERVICE_OPTIONS = [
    { id: "Maid", labelEn: "Maid / Housekeeping", labelHi: "कामवाली बाई / सफाई", icon: Sparkles, color: "bg-pink-50 text-pink-700 border-pink-200" },
    { id: "Cook", labelEn: "Cook / Chef", labelHi: "रसोइया / कुक", icon: Utensils, color: "bg-orange-50 text-orange-700 border-orange-200" },
    { id: "Babysitter", labelEn: "Babysitter / Nanny", labelHi: "आया / बेबीसिटर", icon: Baby, color: "bg-rose-50 text-rose-700 border-rose-200" },
    { id: "Driver", labelEn: "Driver", labelHi: "ड्राइवर", icon: Car, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    { id: "Car Cleaner", labelEn: "Car Cleaner", labelHi: "गाड़ी धोने वाला", icon: Car, color: "bg-teal-50 text-teal-700 border-teal-200" },
    { id: "Milkman", labelEn: "Milkman", labelHi: "दूधवाला", icon: Coffee, color: "bg-amber-50 text-amber-700 border-amber-200" },
    { id: "Newspaper", labelEn: "Newspaper Delivery", labelHi: "अखबार वाला", icon: Newspaper, color: "bg-blue-50 text-blue-700 border-blue-200" },
    { id: "Laundry", labelEn: "Laundry / Ironing", labelHi: "धोबी / इस्त्री वाला", icon: Shirt, color: "bg-purple-50 text-purple-700 border-purple-200" },
    { id: "Society Staff", labelEn: "Society Staff", labelHi: "सोसायटी स्टाफ", icon: Wrench, color: "bg-slate-50 text-slate-700 border-slate-200" },
    { id: "Other", labelEn: "Other Staff", labelHi: "अन्य स्टाफ", icon: Users, color: "bg-gray-50 text-gray-700 border-gray-200" },
];

function getServiceOption(type) {
    return SERVICE_OPTIONS.find((s) => s.id === type) || SERVICE_OPTIONS[0];
}

function formatEventTime(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    } catch {
        return "";
    }
}

export function StaffQRModal({ helper, onClose, seniorMode = true, lang = "en" }) {
    if (!helper) return null;
    const isHi = lang === "hi";
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${encodeURIComponent(helper.pass_code)}`;
    const flatsStr = Array.isArray(helper.flats) && helper.flats.length ? helper.flats.join(", ") : "Society";

    return createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/65 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-fade-in" onClick={onClose}>
            <div
                className={`bg-white rounded-3xl border border-nest-line w-full max-w-sm ${seniorMode ? "p-5 sm:p-6" : "p-4 sm:p-5"} text-center shadow-2xl my-auto animate-scale-up`}
                onClick={(e) => e.stopPropagation()}
                data-testid="staff-qr-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between pb-2 border-b border-nest-line">
                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-300">
                        {isHi ? "स्थायी गेट पास" : "Permanent Gate Pass"}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink transition cursor-pointer"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* QR Code Box */}
                <div className="my-3.5 p-3.5 rounded-2xl bg-nest-alt/40 border-2 border-dashed border-emerald-500/40 inline-block shadow-xs">
                    <img
                        src={qrUrl}
                        alt="Staff Pass QR"
                        className="h-44 w-44 mx-auto rounded-xl bg-white p-2 shadow-2xs"
                    />
                </div>

                {/* Staff Details */}
                <h3 className={`font-black text-nest-ink tracking-tight ${seniorMode ? "text-xl sm:text-2xl" : "text-lg"}`}>
                    {helper.name}
                </h3>
                <p className="text-xs font-bold text-nest-clay mt-0.5">
                    {helper.service_type} · <span className="text-amber-900 bg-amber-100 border border-amber-300 px-1.5 py-0.2 rounded font-mono">{flatsStr}</span>
                </p>

                {/* Passcode Chip */}
                <div className="mt-3 p-2.5 rounded-xl bg-nest-alt border border-nest-line flex items-center justify-between">
                    <span className="text-xs text-nest-ink2 font-medium">{isHi ? "पासकोड" : "Passcode"}</span>
                    <span className="font-mono text-lg font-black tracking-widest text-nest-ink">{helper.pass_code}</span>
                </div>

                <p className="text-[11px] text-nest-ink3 mt-2">
                    {isHi ? "गार्ड इस QR को कैमरे से स्कैन करके 1-टैप में हाजिरी दर्ज कर सकते हैं।" : "Guard can scan this QR or enter the code at the gate for 1-tap check-in."}
                </p>

                {/* Actions: WhatsApp & Done */}
                <div className="mt-4 pt-3 border-t border-nest-line flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className={`flex-1 rounded-2xl border border-nest-line bg-nest-alt/70 hover:bg-nest-alt text-nest-ink font-bold transition cursor-pointer ${
                            seniorMode ? "h-12 text-sm" : "h-10 text-xs"
                        }`}
                    >
                        {isHi ? "संपन्न / बंद करें" : "Done / Close"}
                    </button>
                    <ShareWhatsApp
                        phone={helper.phone}
                        label={isHi ? "स्टाफ को भेजें" : "Send Pass"}
                        text={`Hello ${helper.name}, your permanent gate entry pass for NeighborNest Society is *${helper.pass_code}*.\nShow this QR/Passcode at the security gate for fast 1-tap entry.\nQR: ${qrUrl}`}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
}

export default function DailyStaffGateModal({ open, onClose, onUpdated, seniorMode = true, lang = "en" }) {
    const isHi = lang === "hi";
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // "all" | "inside" | "outside"
    const [showRegisterForm, setShowRegisterForm] = useState(false);
    const [activePhotoStaff, setActivePhotoStaff] = useState(null);
    const [activeQRHelper, setActiveQRHelper] = useState(null);

    // Register Form State
    const [regForm, setRegForm] = useState({
        name: "",
        service_type: "Maid",
        phone: "",
        vehicle_type: "none",
        vehicle_no: "",
    });

    // Assigned Flats in Dropdown + Number in Numeric Keyboard
    const [selectedTower, setSelectedTower] = useState("A");
    const [flatNo, setFlatNo] = useState("");
    const [assignedFlats, setAssignedFlats] = useState([]);

    const [regPhotoFile, setRegPhotoFile] = useState(null);
    const [regPhotoPreview, setRegPhotoPreview] = useState(null);
    const [registering, setRegistering] = useState(false);

    // Speech-to-text for registration
    const [listeningField, setListeningField] = useState(null);

    const loadStaff = async () => {
        setLoading(true);
        try {
            const res = await api.get("/daily-help");
            setStaffList(res.data || []);
        } catch {
            toast.error(isHi ? "स्टाफ सूची लोड नहीं हो सकी" : "Failed to load staff list");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadStaff();
            setShowRegisterForm(false);
            setSearchQuery("");
            setStatusFilter("all");
            setActiveQRHelper(null);
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [open]);

    const insideCount = useMemo(() => staffList.filter((s) => s.is_inside).length, [staffList]);
    const outsideCount = useMemo(() => staffList.filter((s) => !s.is_inside).length, [staffList]);

    const filteredStaff = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return staffList.filter((s) => {
            // Status filter
            if (statusFilter === "inside" && !s.is_inside) return false;
            if (statusFilter === "outside" && s.is_inside) return false;

            // Search query match
            if (!q) return true;
            const nameMatch = (s.name || "").toLowerCase().includes(q);
            const serviceMatch = (s.service_type || "").toLowerCase().includes(q);
            const phoneMatch = (s.phone || "").toLowerCase().includes(q);
            const codeMatch = (s.pass_code || "").toLowerCase().includes(q);
            const flatsMatch = Array.isArray(s.flats) && s.flats.some((f) => f.toLowerCase().includes(q));
            const vehMatch = (s.vehicle_no || "").toLowerCase().includes(q);

            return nameMatch || serviceMatch || phoneMatch || codeMatch || flatsMatch || vehMatch;
        });
    }, [staffList, searchQuery, statusFilter]);

    const handleToggleAttendance = async (staff) => {
        const isCheckingIn = !staff.is_inside;
        const endpoint = isCheckingIn
            ? `/daily-help/${staff.help_id}/check-in`
            : `/daily-help/${staff.help_id}/check-out`;

        setActionLoadingId(staff.help_id);
        try {
            await api.post(endpoint);
            toast.success(
                isCheckingIn
                    ? (isHi ? `✓ ${staff.name} अंदर दर्ज (Check-in)` : `✓ Checked IN: ${staff.name}`)
                    : (isHi ? `✓ ${staff.name} बाहर दर्ज (Check-out)` : `✓ Checked OUT: ${staff.name}`)
            );
            await loadStaff();
            if (onUpdated) onUpdated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "हाजिरी दर्ज करने में त्रुटि" : "Failed to update attendance"));
        } finally {
            setActionLoadingId(null);
        }
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setRegPhotoFile(file);
            setRegPhotoPreview(URL.createObjectURL(file));
        } else {
            setRegPhotoFile(null);
            setRegPhotoPreview(null);
        }
    };

    const handleAddFlat = () => {
        const num = flatNo.trim().replace(/[^0-9]/g, "");
        if (!num) {
            toast.error(isHi ? "फ्लैट नंबर लिखें (उदा. 402)" : "Enter flat number (e.g. 402)");
            return;
        }
        const fullFlat = `${selectedTower}-${num}`;
        if (!assignedFlats.includes(fullFlat)) {
            setAssignedFlats((prev) => [...prev, fullFlat]);
            setFlatNo("");
            toast.success(isHi ? `फ्लैट ${fullFlat} जोड़ा गया` : `Added Flat ${fullFlat}`);
        } else {
            toast.info(isHi ? `फ्लैट ${fullFlat} पहले से जुड़ा है` : `Flat ${fullFlat} already added`);
        }
    };

    const handleRemoveFlat = (f) => {
        setAssignedFlats((prev) => prev.filter((x) => x !== f));
    };

    const handleRegisterStaff = async (e) => {
        e.preventDefault();
        if (!regForm.name.trim()) {
            toast.error(isHi ? "स्टाफ का नाम दर्ज करें" : "Staff name is required");
            return;
        }

        // Gather all assigned flats
        let finalFlats = [...assignedFlats];
        if (flatNo.trim()) {
            const num = flatNo.trim().replace(/[^0-9]/g, "");
            if (num) {
                const fullFlat = `${selectedTower}-${num}`;
                if (!finalFlats.includes(fullFlat)) {
                    finalFlats.push(fullFlat);
                }
            }
        }

        if (finalFlats.length === 0) {
            toast.error(isHi ? "कम से कम एक फ्लैट नंबर दर्ज करें (उदा. 402)" : "Please assign at least one flat number (e.g. 402)");
            return;
        }

        setRegistering(true);
        try {
            const payload = {
                name: regForm.name.trim(),
                phone: regForm.phone.trim() || null,
                service_type: regForm.service_type || "Maid",
                flats: finalFlats,
                vehicle_type: regForm.vehicle_type || "none",
                vehicle_no: regForm.vehicle_type !== "none" && regForm.vehicle_no ? regForm.vehicle_no.trim().toUpperCase() : null,
                status: "active",
            };

            const res = await api.post("/daily-help", payload);
            const created = res.data;

            // Upload photo if provided
            if (regPhotoFile && created.help_id) {
                try {
                    const fd = new FormData();
                    fd.append("photo", regPhotoFile);
                    await api.post(`/daily-help/${created.help_id}/photo`, fd);
                    created.has_photo = true;
                    created.photo_url = `/api/daily-help/${created.help_id}/photo`;
                } catch {
                    console.warn("Photo upload failed after helper created");
                }
            }

            toast.success(
                isHi
                    ? `✓ नया स्टाफ पंजीकृत! पासकोड: ${created.pass_code}`
                    : `✓ Registered ${created.name}! Passcode: ${created.pass_code}`
            );

            // Reset form
            setRegForm({
                name: "",
                service_type: "Maid",
                phone: "",
                vehicle_type: "none",
                vehicle_no: "",
            });
            setSelectedTower("A");
            setFlatNo("");
            setAssignedFlats([]);
            setRegPhotoFile(null);
            setRegPhotoPreview(null);
            setShowRegisterForm(false);

            // Pop up the QR Code Modal immediately for the newly registered staff member!
            setActiveQRHelper(created);

            await loadStaff();
            if (onUpdated) onUpdated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "पंजीकरण विफल रहा" : "Registration failed"));
        } finally {
            setRegistering(false);
        }
    };

    // Voice recognition for quick name/phone dictation in register form
    const startVoice = (field) => {
        const SpeechRecognition = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
        if (!SpeechRecognition) {
            toast.error(isHi ? "इस ब्राउज़र में आवाज़ इनपुट समर्थित नहीं है" : "Voice input is not supported in this browser");
            return;
        }
        if (listeningField === field) {
            setListeningField(null);
            return;
        }

        try {
            const rec = new SpeechRecognition();
            rec.lang = isHi ? "hi-IN" : "en-IN";
            rec.continuous = false;
            rec.interimResults = false;

            rec.onstart = () => {
                setListeningField(field);
                toast.info(isHi ? "बोलें..." : "Listening... speak now");
            };

            rec.onresult = (e) => {
                const text = e.results[0][0].transcript;
                if (field === "name") {
                    const cleanName = formatSpokenName(text) || text;
                    setRegForm((p) => ({ ...p, name: cleanName }));
                    toast.success(cleanName);
                } else if (field === "phone") {
                    const digits = formatSpokenPhone(text) || text.replace(/\D/g, "");
                    setRegForm((p) => ({ ...p, phone: digits }));
                    toast.success(digits);
                }
            };

            rec.onerror = () => setListeningField(null);
            rec.onend = () => setListeningField(null);
            rec.start();
        } catch {
            setListeningField(null);
        }
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 overflow-y-auto animate-fade-in" onClick={onClose}>
            <div
                className={`bg-white rounded-t-3xl sm:rounded-3xl border border-nest-line w-full max-w-2xl ${
                    seniorMode ? "p-4 sm:p-6" : "p-4 sm:p-5"
                } max-h-[92vh] flex flex-col shadow-2xl my-auto animate-scale-up`}
                onClick={(e) => e.stopPropagation()}
                data-testid="daily-staff-modal"
            >
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-3 border-b border-nest-line shrink-0 gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`rounded-2xl bg-emerald-500/10 text-emerald-800 grid place-items-center shrink-0 ${seniorMode ? "h-12 w-12" : "h-10 w-10"}`}>
                            <Users size={seniorMode ? 24 : 18} />
                        </div>
                        <div className="min-w-0">
                            <span className={`font-black uppercase tracking-wider text-emerald-700 block ${seniorMode ? "text-xs" : "text-[10px]"}`}>
                                {isHi ? "गेट उपस्थिति डेस्क" : "Gate Attendance Desk"}
                            </span>
                            <h3 className={`font-black text-nest-ink tracking-tight ${seniorMode ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"}`}>
                                {isHi ? "दैनिक स्टाफ" : "Daily Staff"}
                            </h3>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowRegisterForm((p) => !p)}
                            className={`rounded-2xl font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs ${
                                seniorMode ? "h-11 px-3.5 sm:px-4 text-xs sm:text-sm" : "h-9 px-3 text-xs"
                            } ${
                                showRegisterForm
                                    ? "bg-neutral-800 text-white"
                                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                            }`}
                            data-testid="toggle-register-staff-btn"
                        >
                            {showRegisterForm ? (
                                <>
                                    <Users size={seniorMode ? 16 : 14} />
                                    <span>{isHi ? "स्टाफ सूची" : "Staff List"}</span>
                                </>
                            ) : (
                                <>
                                    <Plus size={seniorMode ? 18 : 15} />
                                    <span>{isHi ? "नया स्टाफ" : "Register Staff"}</span>
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            className={`rounded-2xl bg-nest-alt/70 hover:bg-nest-alt text-nest-ink grid place-items-center transition cursor-pointer shrink-0 ${
                                seniorMode ? "h-11 w-11" : "h-9 w-9"
                            }`}
                            aria-label="Close"
                            data-testid="daily-staff-modal-close"
                        >
                            <X size={seniorMode ? 22 : 18} />
                        </button>
                    </div>
                </div>

                {/* Sub-Header: Staff List View vs Register View */}
                {!showRegisterForm ? (
                    <div className="flex flex-col flex-1 min-h-0">
                        {/* Search & Filter Bar */}
                        <div className="mt-3 space-y-2 shrink-0">
                            {/* Search Input */}
                            <div className="relative">
                                <Search size={seniorMode ? 20 : 16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-nest-ink3" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={isHi ? "नाम, काम, फ्लैट या फोन से खोजें..." : "Search staff by name, service, flat or phone..."}
                                    className={`w-full bg-nest-alt/60 rounded-2xl border border-nest-line focus:border-emerald-600 focus:bg-white outline-none font-medium text-nest-ink placeholder:text-nest-ink3 transition ${
                                        searchQuery ? "pr-10" : ""
                                    } ${
                                        seniorMode ? "h-13 sm:h-14 pl-11 text-base sm:text-lg" : "py-2.5 pl-10 text-xs sm:text-sm"
                                    }`}
                                    data-testid="daily-staff-search-input"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-nest-ink3 hover:text-nest-ink rounded-lg"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>

                            {/* Status Filter Chips */}
                            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter("all")}
                                    className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap ${
                                        seniorMode ? "h-10 px-3.5 text-xs sm:text-sm" : "px-3 py-1.5 text-xs"
                                    } ${
                                        statusFilter === "all"
                                            ? "bg-nest-ink text-white shadow-xs"
                                            : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                                    }`}
                                >
                                    {isHi ? "सभी" : "All"} ({staffList.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter("inside")}
                                    className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                                        seniorMode ? "h-10 px-3.5 text-xs sm:text-sm" : "px-3 py-1.5 text-xs"
                                    } ${
                                        statusFilter === "inside"
                                            ? "bg-emerald-600 text-white shadow-xs"
                                            : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200"
                                    }`}
                                >
                                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                    <span>{isHi ? "अंदर हैं" : "Inside"} ({insideCount})</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter("outside")}
                                    className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap ${
                                        seniorMode ? "h-10 px-3.5 text-xs sm:text-sm" : "px-3 py-1.5 text-xs"
                                    } ${
                                        statusFilter === "outside"
                                            ? "bg-neutral-800 text-white shadow-xs"
                                            : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                                    }`}
                                >
                                    {isHi ? "बाहर हैं" : "Outside"} ({outsideCount})
                                </button>
                            </div>
                        </div>

                        {/* Staff Cards Feed */}
                        <div className="mt-3 flex-1 overflow-y-auto space-y-2.5 overscroll-contain pr-1">
                            {loading ? (
                                <div className="py-14 text-center text-nest-ink2 space-y-2">
                                    <div className="h-6 w-6 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin mx-auto" />
                                    <p className={seniorMode ? "text-base font-bold" : "text-xs"}>
                                        {isHi ? "स्टाफ सूची लोड हो रही है…" : "Loading staff members…"}
                                    </p>
                                </div>
                            ) : filteredStaff.length === 0 ? (
                                <div className="py-12 text-center text-nest-ink2 space-y-2 bg-nest-alt/30 rounded-2xl border border-dashed border-nest-line p-4">
                                    <Users size={32} className="mx-auto text-nest-ink3" />
                                    <p className={`font-black text-nest-ink ${seniorMode ? "text-base sm:text-lg" : "text-sm"}`}>
                                        {isHi ? "कोई स्टाफ नहीं मिला" : "No staff members found"}
                                    </p>
                                    <p className={`text-nest-ink3 ${seniorMode ? "text-xs sm:text-sm" : "text-xs"}`}>
                                        {searchQuery
                                            ? (isHi ? "आपकी खोज के अनुसार कोई स्टाफ नहीं मिला।" : "No staff matching your search filter.")
                                            : (isHi ? "सोसायटी में अभी कोई स्टाफ पंजीकृत नहीं है।" : "No daily help staff registered yet.")}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowRegisterForm(true)}
                                        className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer shadow-xs"
                                    >
                                        <Plus size={14} />
                                        <span>{isHi ? "नया स्टाफ जोड़ें" : "Register First Staff Member"}</span>
                                    </button>
                                </div>
                            ) : (
                                filteredStaff.map((staff) => {
                                    const meta = getServiceOption(staff.service_type);
                                    const IconComp = meta.icon;
                                    const flatsStr = Array.isArray(staff.flats) && staff.flats.length ? staff.flats.join(", ") : "All Flats";
                                    const isInside = Boolean(staff.is_inside);
                                    const isToggling = actionLoadingId === staff.help_id;
                                    const hasPhoto = Boolean(staff.has_photo || staff.photo_path || staff.photo_url);

                                    return (
                                        <div
                                            key={staff.help_id}
                                            className={`rounded-2xl border transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                                isInside
                                                    ? "bg-emerald-50/40 border-emerald-300/80 shadow-xs"
                                                    : "bg-white border-nest-line hover:border-nest-line/80 shadow-2xs"
                                            } ${seniorMode ? "p-3.5 sm:p-4" : "p-3"}`}
                                            data-testid={`daily-staff-card-${staff.help_id}`}
                                        >
                                            {/* Left details */}
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                {/* Staff Photo / Avatar */}
                                                {hasPhoto ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setActivePhotoStaff(staff)}
                                                        className={`rounded-2xl overflow-hidden border-2 shrink-0 bg-nest-alt relative group cursor-pointer ${
                                                            isInside ? "border-emerald-500" : "border-nest-line"
                                                        } ${seniorMode ? "h-14 w-14 sm:h-16 sm:w-16" : "h-11 w-11 sm:h-12 sm:w-12"}`}
                                                        title="Click to view photo"
                                                    >
                                                        <img
                                                            src={getVisitorPhotoSrc(staff)}
                                                            alt={staff.name}
                                                            className="h-full w-full object-cover group-hover:scale-105 transition"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = "none";
                                                                if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "grid";
                                                            }}
                                                        />
                                                        <div style={{ display: "none" }} className="h-full w-full bg-nest-alt place-items-center text-nest-ink3">
                                                            <User size={seniorMode ? 22 : 16} />
                                                        </div>
                                                    </button>
                                                ) : (
                                                    <div className={`rounded-2xl border-2 shrink-0 grid place-items-center ${
                                                        isInside ? "border-emerald-400 bg-emerald-100 text-emerald-800" : "border-nest-line bg-nest-alt text-nest-ink3"
                                                    } ${seniorMode ? "h-14 w-14 sm:h-16 sm:w-16" : "h-11 w-11 sm:h-12 sm:w-12"}`}>
                                                        <User size={seniorMode ? 24 : 18} />
                                                    </div>
                                                )}

                                                {/* Info column */}
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <p className={`font-black text-nest-ink truncate leading-tight ${
                                                            seniorMode ? "text-base sm:text-lg" : "text-sm sm:text-base"
                                                        }`}>
                                                            {staff.name}
                                                        </p>

                                                        {/* Service Badge */}
                                                        <span className={`inline-flex items-center gap-1 font-bold rounded-lg border ${meta.color} ${
                                                            seniorMode ? "text-[11px] sm:text-xs px-2 py-0.5" : "text-[10px] px-1.5 py-0.2"
                                                        }`}>
                                                            <IconComp size={seniorMode ? 13 : 11} className="shrink-0" />
                                                            <span>{isHi ? meta.labelHi : meta.labelEn}</span>
                                                        </span>

                                                        {/* Passcode Chip */}
                                                        <span className={`font-mono font-bold rounded-lg bg-nest-alt border border-nest-line text-nest-ink ${
                                                            seniorMode ? "text-[11px] sm:text-xs px-2 py-0.5" : "text-[9px] px-1.5 py-0.2"
                                                        }`}>
                                                            {staff.pass_code}
                                                        </span>
                                                    </div>

                                                    {/* Flats & Vehicle */}
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap text-nest-ink2">
                                                        <span className={`inline-flex items-center gap-1 font-bold bg-amber-50 border border-amber-200 text-amber-900 rounded-md ${
                                                            seniorMode ? "text-xs px-2 py-0.5" : "text-[10px] px-1.5 py-0.2"
                                                        }`}>
                                                            <Building2 size={seniorMode ? 13 : 10} className="text-amber-700 shrink-0" />
                                                            <span className="truncate max-w-[160px] sm:max-w-[220px]">{flatsStr}</span>
                                                        </span>

                                                        {staff.phone && (
                                                            <a
                                                                href={`tel:${staff.phone.replace(/[^0-9+]/g, "")}`}
                                                                className={`inline-flex items-center gap-1 font-bold text-nest-clay hover:underline ${
                                                                    seniorMode ? "text-xs" : "text-[10px]"
                                                                }`}
                                                            >
                                                                <Phone size={seniorMode ? 12 : 9} />
                                                                <span>{staff.phone}</span>
                                                            </a>
                                                        )}

                                                        {staff.vehicle_no && (
                                                            <span className={`inline-flex items-center gap-1 font-mono font-bold bg-nest-alt rounded border border-nest-line ${
                                                                seniorMode ? "text-xs px-1.5 py-0.5" : "text-[9px] px-1"
                                                            }`}>
                                                                {staff.vehicle_type === "bike" ? <Bike size={seniorMode ? 12 : 10} /> : <Car size={seniorMode ? 12 : 10} />}
                                                                <span>{staff.vehicle_no}</span>
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Last entry/exit timestamp */}
                                                    {(staff.last_entry_at || staff.last_exit_at) && (
                                                        <p className={`text-nest-ink3 mt-0.5 flex items-center gap-1 ${seniorMode ? "text-[11px] sm:text-xs" : "text-[9px]"}`}>
                                                            <Clock size={seniorMode ? 12 : 9} />
                                                            <span>
                                                                {isInside
                                                                    ? `${isHi ? "प्रवेश" : "In"}: ${formatEventTime(staff.last_entry_at)}`
                                                                    : `${isHi ? "प्रस्थान" : "Out"}: ${formatEventTime(staff.last_exit_at)}`}
                                                            </span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Bar: Status Badge, View QR Pass & 1-Tap Attendance */}
                                            <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0 pt-2.5 sm:pt-0 mt-1 sm:mt-0 border-t sm:border-t-0 border-nest-line/60">
                                                {/* Status indicator pill */}
                                                <span className={`inline-flex items-center gap-1.5 font-black uppercase tracking-wider rounded-xl ${
                                                    seniorMode ? "text-xs px-2.5 py-1.5" : "text-[10px] px-2 py-1"
                                                } ${
                                                    isInside
                                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                                        : "bg-stone-100 text-stone-600 border border-stone-300"
                                                }`}>
                                                    <span className={`h-2 w-2 rounded-full ${isInside ? "bg-emerald-600 animate-pulse" : "bg-stone-400"}`} />
                                                    <span>{isInside ? (isHi ? "अंदर हैं" : "INSIDE") : (isHi ? "बाहर हैं" : "OUTSIDE")}</span>
                                                </span>

                                                <div className="flex items-center gap-2">
                                                    {/* View QR Code Button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveQRHelper(staff)}
                                                        className={`font-bold rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 active:scale-95 border border-nest-line bg-white hover:bg-nest-alt text-nest-ink shadow-2xs ${
                                                            seniorMode ? "h-11 sm:h-12 px-3 sm:px-3.5 text-xs sm:text-sm" : "h-9 sm:h-10 px-2.5 sm:px-3 text-xs"
                                                        }`}
                                                        title={isHi ? "QR पास देखें या WhatsApp करें" : "View or WhatsApp QR Pass"}
                                                        data-testid={`daily-staff-qr-btn-${staff.help_id}`}
                                                    >
                                                        <QrCode size={seniorMode ? 16 : 14} className="text-emerald-700" />
                                                        <span>{isHi ? "QR पास" : "QR Pass"}</span>
                                                    </button>

                                                    {/* Giant 1-Tap Toggle Attendance Button */}
                                                    <button
                                                        type="button"
                                                        disabled={isToggling}
                                                        onClick={() => handleToggleAttendance(staff)}
                                                        className={`font-black uppercase tracking-wider rounded-xl transition cursor-pointer flex items-center justify-center gap-2 active:scale-95 shadow-md ${
                                                            seniorMode ? "h-11 sm:h-12 px-4 sm:px-5 text-xs sm:text-sm" : "h-9 sm:h-10 px-3.5 sm:px-4 text-xs"
                                                        } ${
                                                            isInside
                                                                ? "bg-rose-600 hover:bg-rose-700 text-white shadow-rose-900/20 border border-rose-700"
                                                                : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-900/20 border border-emerald-700"
                                                        }`}
                                                        data-testid={`daily-staff-toggle-btn-${staff.help_id}`}
                                                    >
                                                        {isToggling ? (
                                                            <>
                                                                <RefreshCw size={seniorMode ? 16 : 13} className="animate-spin text-white" />
                                                                <span>{isInside ? (isHi ? "प्रस्थान…" : "Exiting…") : (isHi ? "प्रवेश…" : "Entering…")}</span>
                                                            </>
                                                        ) : isInside ? (
                                                            <>
                                                                <LogOut size={seniorMode ? 17 : 14} />
                                                                <span>{isHi ? "चेक-आउट" : "CHECK OUT"}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <LogIn size={seniorMode ? 17 : 14} />
                                                                <span>{isHi ? "चेक-इन" : "CHECK IN"}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                ) : (
                    /* Inline Staff Registration Form */
                    <form onSubmit={handleRegisterStaff} className="mt-3 overflow-y-auto pr-1 flex-1 space-y-3.5">
                        <div className="bg-emerald-500/10 border border-emerald-400/40 rounded-2xl p-3 flex items-center justify-between gap-2">
                            <div>
                                <h4 className={`font-black text-emerald-950 ${seniorMode ? "text-base sm:text-lg" : "text-sm"}`}>
                                    {isHi ? "नया दैनिक स्टाफ पंजीकरण" : "Register New Daily Help"}
                                </h4>
                                <p className={`text-emerald-800 ${seniorMode ? "text-xs sm:text-sm" : "text-xs"}`}>
                                    {isHi ? "गार्ड तुरंत नया स्टाफ जोड़ सकते हैं और QR पास कोड बना सकते हैं।" : "Create gate pass and instant QR code for maid, cook, driver, or staff."}
                                </p>
                            </div>
                            <span className="text-2xl select-none">🧹</span>
                        </div>

                        {/* Photo capture block */}
                        <div className="rounded-2xl border-2 border-dashed border-nest-line p-3 bg-nest-alt/20">
                            <label className="block cursor-pointer">
                                <span className={`block font-bold text-nest-ink mb-1.5 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                    📷 {isHi ? "स्टाफ फोटो (वैकल्पिक)" : "Staff Photo (Optional)"}
                                </span>
                                {regPhotoPreview ? (
                                    <div className="relative">
                                        <img src={regPhotoPreview} alt="Staff preview" className="w-full h-44 object-cover rounded-xl border border-nest-line" />
                                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-lg">
                                            {isHi ? "फोटो बदलें" : "Change Photo"}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-32 rounded-xl border-2 border-dashed border-emerald-400/50 bg-white grid place-items-center text-nest-ink hover:bg-emerald-50/50 transition">
                                        <div className="text-center p-2">
                                            <Camera size={26} className="text-emerald-700 mx-auto mb-1" />
                                            <p className={`font-bold text-nest-ink ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                                {isHi ? "कैमरा खोलें / फोटो खींचें" : "Tap to Open Camera / Take Photo"}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handlePhotoSelect}
                                    className="mt-2 block w-full text-xs text-nest-ink2 cursor-pointer"
                                />
                            </label>
                        </div>

                        {/* Staff Name with Mic */}
                        <div>
                            <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {isHi ? "स्टाफ का पूरा नाम *" : "Staff Full Name *"}
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    required
                                    value={regForm.name}
                                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                                    placeholder={isHi ? "उदा. सुनीता देवी" : "e.g. Sunita Devi"}
                                    className={`w-full bg-nest-alt/40 border border-nest-line focus:border-emerald-600 focus:bg-white rounded-xl outline-none font-bold text-nest-ink transition ${
                                        seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => startVoice("name")}
                                    className={`shrink-0 rounded-xl font-bold grid place-items-center transition cursor-pointer active:scale-95 ${
                                        seniorMode ? "h-13 w-13 sm:h-14 sm:w-14" : "h-10 w-10"
                                    } ${
                                        listeningField === "name"
                                            ? "bg-red-500 text-white animate-pulse"
                                            : "bg-emerald-50 text-emerald-800 border border-emerald-300"
                                    }`}
                                    title="Speak name"
                                >
                                    {listeningField === "name" ? <MicOff size={20} /> : <Mic size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Service Type Dropdown */}
                        <div>
                            <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {isHi ? "काम का प्रकार (Service) *" : "Service Type *"}
                            </label>
                            <select
                                value={regForm.service_type}
                                onChange={(e) => setRegForm({ ...regForm, service_type: e.target.value })}
                                className={`w-full bg-nest-alt/40 border border-nest-line focus:border-emerald-600 focus:bg-white rounded-xl outline-none font-bold text-nest-ink transition cursor-pointer ${
                                    seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                }`}
                            >
                                {SERVICE_OPTIONS.map((opt) => (
                                    <option key={opt.id} value={opt.id}>
                                        {isHi ? opt.labelHi : opt.labelEn}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Assigned Flats with Tower Dropdown and Numeric Flat Number Keyboard */}
                        <div className="p-3 sm:p-3.5 rounded-2xl border border-nest-line bg-nest-alt/30 space-y-2">
                            <div className="flex items-center justify-between">
                                <label className={`block font-bold text-nest-ink ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                    {isHi ? "असाइन किए गए फ्लैट (Assigned Flats) *" : "Assigned Flats *"}
                                </label>
                                <span className="text-[11px] font-semibold text-nest-ink3">
                                    {isHi ? "टावर चुनें व नंबर लिखें" : "Select Tower & Flat #"}
                                </span>
                            </div>

                            {/* Tower Dropdown & Numeric Flat Input */}
                            <div className="flex items-center gap-2">
                                {/* Tower Dropdown */}
                                <select
                                    value={selectedTower}
                                    onChange={(e) => setSelectedTower(e.target.value)}
                                    className={`bg-white border border-nest-line focus:border-emerald-600 rounded-xl outline-none font-bold text-nest-ink transition cursor-pointer shrink-0 ${
                                        seniorMode ? "h-13 sm:h-14 px-3 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                    }`}
                                >
                                    <option value="A">Tower A</option>
                                    <option value="B">Tower B</option>
                                    <option value="C">Tower C</option>
                                    <option value="D">Tower D</option>
                                    <option value="E">Tower E</option>
                                    <option value="F">Tower F</option>
                                </select>

                                {/* Flat Number Input with Numeric Keyboard */}
                                <div className="relative flex-1 min-w-0">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={flatNo}
                                        onChange={(e) => setFlatNo(e.target.value.replace(/[^0-9]/g, ""))}
                                        placeholder={isHi ? "फ्लैट नंबर (उदा. 402)" : "Flat No. (e.g. 402)"}
                                        className={`w-full bg-white border border-nest-line focus:border-emerald-600 rounded-xl outline-none font-bold text-nest-ink transition ${
                                            seniorMode ? "h-13 sm:h-14 px-3.5 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                        }`}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddFlat();
                                            }
                                        }}
                                    />
                                </div>

                                {/* Add Flat Button */}
                                <button
                                    type="button"
                                    onClick={handleAddFlat}
                                    className={`shrink-0 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition flex items-center justify-center gap-1 active:scale-95 cursor-pointer shadow-xs ${
                                        seniorMode ? "h-13 sm:h-14 px-3.5 sm:px-4 text-xs sm:text-sm" : "h-10 px-3 text-xs"
                                    }`}
                                    title="Add Flat to list"
                                >
                                    <Plus size={16} />
                                    <span>{isHi ? "जोड़ें" : "Add"}</span>
                                </button>
                            </div>

                            {/* Display Added Flat Pills */}
                            {assignedFlats.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {assignedFlats.map((f, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-950 border border-amber-300 font-bold px-2.5 py-1 rounded-xl text-xs shadow-2xs"
                                        >
                                            <Building2 size={12} className="text-amber-700" />
                                            <span>Flat {f}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFlat(f)}
                                                className="h-4 w-4 grid place-items-center rounded-full hover:bg-amber-200 text-amber-800 transition cursor-pointer"
                                                title="Remove flat"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[11px] text-nest-ink3 italic pt-0.5">
                                    {isHi ? "उदा. टावर A और 402 लिखकर 'जोड़ें' दबाएं (एक से अधिक फ्लैट जोड़ सकते हैं)।" : "e.g. Select Tower A, type 402 and tap 'Add' (you can add multiple flats)."}
                                </p>
                            )}
                        </div>

                        {/* Mobile Number with Mic */}
                        <div>
                            <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {isHi ? "मोबाइल नंबर (वैकल्पिक)" : "Mobile Number (Optional)"}
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={regForm.phone}
                                    onChange={(e) => setRegForm({ ...regForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                                    placeholder={isHi ? "10 अंकों का मोबाइल नंबर" : "10-digit mobile number"}
                                    className={`w-full bg-nest-alt/40 border border-nest-line focus:border-emerald-600 focus:bg-white rounded-xl outline-none font-mono font-bold text-nest-ink transition ${
                                        seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg tracking-wider" : "py-2 px-3 text-sm"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => startVoice("phone")}
                                    className={`shrink-0 rounded-xl font-bold grid place-items-center transition cursor-pointer active:scale-95 ${
                                        seniorMode ? "h-13 w-13 sm:h-14 sm:w-14" : "h-10 w-10"
                                    } ${
                                        listeningField === "phone"
                                            ? "bg-red-500 text-white animate-pulse"
                                            : "bg-emerald-50 text-emerald-800 border border-emerald-300"
                                    }`}
                                    title="Speak phone"
                                >
                                    {listeningField === "phone" ? <MicOff size={20} /> : <Mic size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Vehicle */}
                        <div className="p-3 rounded-2xl border border-nest-line bg-nest-alt/30 space-y-2">
                            <label className={`block font-bold text-nest-ink ${seniorMode ? "text-xs sm:text-sm" : "text-[11px]"}`}>
                                {isHi ? "स्टाफ वाहन (साइकिल / बाइक / पैदल)" : "Staff Vehicle"}
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setRegForm({ ...regForm, vehicle_type: "none", vehicle_no: "" })}
                                    className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                        seniorMode ? "h-11 text-xs sm:text-sm" : "py-1.5 text-xs"
                                    } ${
                                        regForm.vehicle_type === "none"
                                            ? "bg-emerald-600 text-white border-emerald-600"
                                            : "bg-white border-nest-line text-nest-ink"
                                    }`}
                                >
                                    <User size={14} /> <span>{isHi ? "पैदल" : "On Foot"}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRegForm({ ...regForm, vehicle_type: "bike" })}
                                    className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                        seniorMode ? "h-11 text-xs sm:text-sm" : "py-1.5 text-xs"
                                    } ${
                                        regForm.vehicle_type === "bike"
                                            ? "bg-emerald-600 text-white border-emerald-600"
                                            : "bg-white border-nest-line text-nest-ink"
                                    }`}
                                >
                                    <Bike size={14} /> <span>{isHi ? "बाइक / साइकिल" : "Bike"}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRegForm({ ...regForm, vehicle_type: "car" })}
                                    className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                        seniorMode ? "h-11 text-xs sm:text-sm" : "py-1.5 text-xs"
                                    } ${
                                        regForm.vehicle_type === "car"
                                            ? "bg-emerald-600 text-white border-emerald-600"
                                            : "bg-white border-nest-line text-nest-ink"
                                    }`}
                                >
                                    <Car size={14} /> <span>{isHi ? "कार" : "Car"}</span>
                                </button>
                            </div>

                            {regForm.vehicle_type !== "none" && (
                                <input
                                    type="text"
                                    placeholder={isHi ? "गाड़ी नंबर (उदा. MH 12 AB 1234)" : "License plate (e.g. MH 12 AB 1234)"}
                                    value={regForm.vehicle_no}
                                    onChange={(e) => setRegForm({ ...regForm, vehicle_no: e.target.value.toUpperCase() })}
                                    className={`w-full bg-white border border-nest-line rounded-xl px-3 font-mono font-bold uppercase outline-none text-nest-ink ${
                                        seniorMode ? "h-12 text-sm sm:text-base" : "py-2 text-xs"
                                    }`}
                                />
                            )}
                        </div>

                        {/* Submit Actions */}
                        <div className="pt-3 border-t border-nest-line flex flex-col sm:flex-row-reverse items-stretch sm:items-center gap-2.5">
                            <button
                                type="submit"
                                disabled={registering}
                                className={`flex-1 rounded-2xl bg-[#1C1B1A] hover:bg-black text-white font-extrabold transition-all duration-150 cursor-pointer shadow-lg hover:shadow-xl active:scale-[0.98] border border-white/10 flex items-center justify-center gap-2.5 ${
                                    seniorMode ? "h-14 sm:h-15 text-sm sm:text-base" : "h-12 sm:h-13 text-xs sm:text-sm"
                                }`}
                                data-testid="submit-register-staff-btn"
                            >
                                {registering ? (
                                    <>
                                        <RefreshCw size={seniorMode ? 20 : 18} className="animate-spin text-emerald-400" />
                                        <span>{isHi ? "QR कोड जनरेट हो रहा है…" : "Generating QR Code…"}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="grid place-items-center h-8 w-8 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-400/30">
                                            <QrCode size={seniorMode ? 19 : 17} />
                                        </span>
                                        <span className="tracking-tight">{isHi ? "स्टाफ जोड़ें और QR कोड बनाएं" : "Register & Generate QR Code"}</span>
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowRegisterForm(false)}
                                className={`sm:w-28 rounded-2xl border border-nest-line bg-nest-alt/70 hover:bg-nest-alt text-nest-ink font-bold transition cursor-pointer flex items-center justify-center ${
                                    seniorMode ? "h-12 sm:h-14 text-sm" : "h-10 sm:h-12 text-xs"
                                }`}
                            >
                                {isHi ? "रद्द करें" : "Cancel"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Staff QR Code Modal (Pops up automatically after registration, and on clicking QR Pass) */}
            <StaffQRModal
                helper={activeQRHelper}
                onClose={() => setActiveQRHelper(null)}
                seniorMode={seniorMode}
                lang={lang}
            />

            {/* Photo Zoom Modal */}
            <VisitorPhotoModal
                visitor={activePhotoStaff}
                onClose={() => setActivePhotoStaff(null)}
                seniorMode={seniorMode}
            />
        </div>,
        document.body
    );
}
